import { useState, useContext, useRef } from 'react'
import { Button, Dialog, IconButton, styled, Typography } from '@mui/material'
import { GridCloseIcon } from '@mui/x-data-grid'
import DownloadIcon from '@mui/icons-material/Download'
import { subirHistoria } from '../../../../AdminHistorias/services/historias.services'
import SnackBarUtils from '../../../../../utils/SnackBarUtils'
import { TenantsContext } from '../../../../../context/tenants.context'
import { useAnalytics } from '../../../../../hooks/useAnalytics'
import StoryEditor from './editStory'
import StoryInitial from './readOnlyStory'

export const StoryWidget = styled(IconButton)(({ theme }) => ({
  color: 'white',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  borderRadius: '50%',
  padding: '5px',
  '&:disabled': {
    color: 'rgba(255, 255, 255, 0.5)',
    backgroundColor: 'rgba(128, 128, 128, 0.3)'
  }
}))

export interface IText {
  value: string
  color: string
  fontSize: number
  fontStyle: string
  fontWeight: string
  alignment: 'center' | 'left' | 'right'
  decoration: string
  position: { x: number; y: number }
  canvasPosition: { x: number; y: number }
}

export interface IEmoji {
  emoji: any
  position: { x: number; y: number }
  canvasPosition: { x: number; y: number }
  canvasEmoji: string
  fontSize: number
}

export const defaultText: IText = {
  value: '',
  color: '#000000',
  fontSize: 24,
  fontStyle: 'normal',
  fontWeight: 'normal',
  alignment: 'center',
  decoration: 'none',
  position: { x: 10, y: 10 },
  canvasPosition: { x: 50, y: 50 }
}

const CrearHistorias = ({
  open,
  setOpen,
  codLocal,
  isMobile,
  updateHistorias
}: {
  open: boolean
  setOpen: (open: boolean) => void
  codLocal: string
  isMobile: boolean
  updateHistorias: (codLocal: string) => Promise<void>
}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [image, setImage] = useState<File | null>(null)
  const [imgDimensions, setImgDimensions] = useState({
    width: 100,
    height: 100
  })
  const [publicando, setPublicando] = useState<boolean>(false)
  const [cropped, setCropped] = useState<boolean>(false)
  const { tenant } = useContext(TenantsContext)
  const editorRef = useRef<{ getCroppedImg: () => Promise<File | undefined> }>(null)

  const generateImageFile = (imagen: File) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    const img = new Image()
    img.src = URL.createObjectURL(imagen)

    img.onload = () => {
      const aspectRatio = img.naturalWidth / img.naturalHeight

      let targetWidth = imgDimensions.width
      let targetHeight = targetWidth / aspectRatio

      if (targetHeight > imgDimensions.height) {
        targetHeight = imgDimensions.height
        targetWidth = targetHeight * aspectRatio
      }

      canvas.width = targetWidth
      canvas.height = targetHeight

      if (ctx) {
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight)
      }

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'edited-image.png', {
            type: 'image/png'
          })
          handleSubmit(file)
        }
      })
    }
  }

  const handlePublicar = async () => {
    if (editorRef.current?.getCroppedImg) {
      setPublicando(true)
      const finalImg = await editorRef.current.getCroppedImg()
      if (finalImg && cropped) {
        handleSubmit(finalImg)
      } else if (image) {
        generateImageFile(image)
      }
    }
  }

  const { track } = useAnalytics()

  const handleSubmit = async (img: File) => {
    if (!img) return

    try {
      setLoading(true)
      await subirHistoria(img, codLocal, tenant).then((res) => {
        if (res.status === 200) {
          SnackBarUtils.success('Historia publicada con éxito')
          updateHistorias(codLocal)
          track('vendor_story_create', {})
        }
      })
    } catch (e) {
      console.log(e)
      SnackBarUtils.error(
        e.message === 'Network Error' || !e.message ? 'Ocurrió un error al subir historia' : e.message
      )
    } finally {
      setLoading(false)
      setPublicando(false)
      handleClose()
    }
  }

  const handleClose = () => {
    setOpen(false)
    setImage(null)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      PaperProps={{
        style: {
          display: 'flex',
          width: isMobile ? '100%' : '600px',
          height: isMobile ? '100%' : '800px',
          padding: 0,
          margin: 0,
          backgroundColor: 'black',
          overflow: 'hidden'
        }
      }}
    >
      <div className="story-creator" style={{ position: 'relative' }}>
        {image ? (
          <StoryEditor
            image={image}
            setImage={setImage}
            imgDimensions={imgDimensions}
            setImgDimensions={setImgDimensions}
            setCropped={setCropped}
            ref={editorRef}
          />
        ) : (
          <StoryInitial setImage={setImage} isMobile={isMobile} />
        )}
        <>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              left: 10,
              bottom: 4,
              fontSize: '8px',
              color: 'white',
              '&:disabled': {
                color: 'rgba(255, 255, 255, 0.5)'
              }
            }}
          >
            <GridCloseIcon />
          </IconButton>
          <Button
            variant="contained"
            onClick={handlePublicar}
            disabled={!image || publicando}
            sx={{
              position: 'absolute',
              bottom: 4,
              right: 10,
              borderRadius: '50px',
              padding: '10px 20px',
              gap: '16px',
              '&:disabled': {
                backgroundColor: '#5983EF',
                color: 'rgba(255, 255, 255, 0.5)'
              }
            }}
          >
            <DownloadIcon />
            <Typography style={{ fontSize: '12px', fontWeight: 'bold' }}>Publicar</Typography>
          </Button>
        </>
      </div>
    </Dialog>
  )
}

export default CrearHistorias
